import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomIcon from '../CustomIcon';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { colors } from '../../theme/colorTokens';

const pathwayKeys = [
  {
    iconName: 'creditCard',
    iconBg: '#EBF0F7',
    iconColor: colors.primary,
    accentColor: colors.primary,
    titleKey: 'home.pathways.item1Title',
    descKey: 'home.pathways.item1Desc',
    primaryLabelKey: 'home.pathways.item1PrimaryLabel',
    primaryRoute: 'blogCredito',
    secondaryLabelKey: 'home.pathways.item1SecondaryLabel',
    secondaryRoute: 'creditCards',
  },
  {
    iconName: 'bank',
    iconBg: colors.successTint,
    iconColor: colors.success,
    accentColor: colors.success,
    titleKey: 'home.pathways.item2Title',
    descKey: 'home.pathways.item2Desc',
    primaryLabelKey: 'home.pathways.item2PrimaryLabel',
    primaryRoute: 'blogCuentas',
    secondaryLabelKey: 'home.pathways.item2SecondaryLabel',
    secondaryRoute: 'banking',
  },
  {
    iconName: 'house',
    iconBg: colors.warningTint,
    iconColor: '#B8860B',
    accentColor: colors.warning,
    titleKey: 'home.pathways.item3Title',
    descKey: 'home.pathways.item3Desc',
    primaryLabelKey: 'home.pathways.item3PrimaryLabel',
    primaryRoute: 'blogHipoteca',
    secondaryLabelKey: 'home.pathways.item3SecondaryLabel',
    secondaryRoute: 'mortgageCalc',
  },
  {
    iconName: 'folder',
    iconBg: colors.errorTint,
    iconColor: colors.error,
    accentColor: colors.error,
    titleKey: 'home.pathways.item4Title',
    descKey: 'home.pathways.item4Desc',
    primaryLabelKey: 'home.pathways.item4PrimaryLabel',
    primaryRoute: 'blogGuiaQuebec',
    secondaryLabelKey: 'home.pathways.item4SecondaryLabel',
    secondaryRoute: 'taxCalendar',
  },
];

const PathwaysSection = () => {
  const lp = useLocalizedPath();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: colors.bgCard,
        pt: { xs: 8, md: 10 },
        pb: { xs: 8, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        {/* Overline */}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            mb: 1.5,
          }}
        >
          {t('home.pathways.overline')}
        </Typography>

        {/* Headline */}
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: colors.textPrimary,
            fontSize: { xs: '28px', md: '36px' },
            lineHeight: 1.15,
            mb: 1.5,
          }}
        >
          {t('home.pathways.title')}
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: '16px',
            color: colors.textSecondary,
            lineHeight: 1.6,
            maxWidth: '50ch',
            mb: 4,
          }}
        >
          {t('home.pathways.subtitle')}
        </Typography>

        {/* Cards Grid */}
        <Grid container spacing={3}>
          {pathwayKeys.map((card) => (
            <Grid item xs={12} md={6} key={card.titleKey}>
              <Box
                sx={{
                  backgroundColor: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderTop: `2px solid ${card.accentColor}`,
                  borderRadius: '14px',
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.03)',
                  transition:
                    'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                  },
                }}
              >
                {/* Icon + Title row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      backgroundColor: card.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CustomIcon name={card.iconName} size={30} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '18px', md: '20px' },
                      fontWeight: 700,
                      color: colors.textPrimary,
                      lineHeight: 1.3,
                    }}
                  >
                    {t(card.titleKey)}
                  </Typography>
                </Box>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: '15px',
                    color: colors.textSecondary,
                    lineHeight: 1.7,
                    flex: 1,
                    mb: 2,
                  }}
                >
                  {t(card.descKey)}
                </Typography>

                {/* Action links */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    pt: 2,
                    borderTop: `1px solid ${colors.border}`,
                  }}
                >
                  <Button
                    component={RouterLink}
                    to={lp(card.primaryRoute)}
                    variant="contained"
                    size="small"
                    endIcon={
                      <ArrowForward sx={{ fontSize: '14px !important' }} />
                    }
                    sx={{
                      height: { xs: '44px', md: '38px' },
                      fontSize: { xs: '14px', md: '13px' },
                      fontWeight: 600,
                      borderRadius: '10px',
                      backgroundColor: colors.primary,
                      color: colors.bgCard,
                      textTransform: 'none',
                      boxShadow: 'none',
                      px: '20px',
                      '&:hover': {
                        backgroundColor: colors.primaryHover,
                        boxShadow: '0 4px 12px rgba(31,58,95,0.3)',
                      },
                    }}
                  >
                    {t(card.primaryLabelKey)}
                  </Button>

                  <Typography
                    component={RouterLink}
                    to={lp(card.secondaryRoute)}
                    sx={{
                      fontSize: { xs: '14px', md: '13px' },
                      fontWeight: 500,
                      color: colors.textSecondary,
                      textDecoration: 'none',
                      transition: 'color 0.2s ease',
                      minHeight: { xs: 44, md: 'auto' },
                      display: 'inline-flex',
                      alignItems: 'center',
                      '&:hover': {
                        color: colors.primary,
                      },
                    }}
                  >
                    {`${t(card.secondaryLabelKey)} →`}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PathwaysSection;
